import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { HiFilter, HiPlus, HiSearch } from "react-icons/hi";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import AddBannerForm from "./AddBannerForm";
import ViewBannerModal from "./ViewBannerModal";
import Pagination from "../../../../Components/Pagination/Pagination";
import { usePermission } from "Context/PermissionContext";

const BannerSkeleton = () => {
  return (
    <>
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-2xl border bg-white p-4 shadow-md"
        >
          <div className="mb-3 h-40 w-full rounded-xl bg-gray-200 sm:h-48"></div>
          <div className="mb-2 h-5 w-3/4 rounded bg-gray-200"></div>
          <div className="mb-1 h-4 w-1/2 rounded bg-gray-200"></div>
          <div className="mb-1 h-4 w-2/3 rounded bg-gray-200"></div>
          <div className="mt-2 h-4 w-1/4 rounded bg-gray-200"></div>
        </div>
      ))}
    </>
  );
};

const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const BannersList = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("All Banners");
  const [addBannerModal, setAddBannerModal] = useState(false);
  const [activeTab, setActiveTab] = useState("Home");
  const [mode, setMode] = useState("add");
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [bannerToView, setBannerToView] = useState(null);
  const [banners, setBanners] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const debouncedSearch = useDebounce(searchTerm, 500);
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
  };
  const { permissions } = usePermission();
  const currentTabPermissions =
    permissions?.coinManagement?.subModule?.manageCoins?.permissions;

  //  Fetch banners with pagination
  const fetchBanners = async (
    category = activeTab,
    page = pagination.currentPage
  ) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/banner/get-all-banner`,
        {
          params: { category, page, limit: 6, search: debouncedSearch },
        }
      );

      setBanners(res.data.data || []);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error("❌ Failed to fetch banners:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners(activeTab, 1);
  }, [activeTab, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAdd = () => {
    setMode("add");
    setSelectedBanner(null);
    setAddBannerModal(true);
  };

  const handleEdit = (banner) => {
    setMode("edit");
    setSelectedBanner(banner);
    setAddBannerModal(true);
  };

  const handleView = (banner) => {
    setBannerToView(banner);
    setViewModalOpen(true);
  };

  // ✅ Delete banner with SweetAlert confirmation
  const handleDelete = async (bannerId) => {
    if (!bannerId) return Swal.fire("No banner selected!", "", "warning");

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This banner will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c51369",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/banner/delete-banner/${bannerId}`
      );
      Swal.fire("Deleted!", "✅ Banner deleted successfully.", "success");
      fetchBanners(activeTab, pagination.currentPage);
    } catch (error) {
      console.error("Delete error:", error.response || error.message);
      Swal.fire("Error!", "❌ Failed to delete banner.", "error");
    }
  };

  const handlePageChange = (newPage) => {
    fetchBanners(activeTab, newPage);
  };

  const filteredBanners = banners.filter((b) =>
    b.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="py-5">
      <AddBannerForm
        addBannerModal={addBannerModal}
        handleClose={() => setAddBannerModal(false)}
        mode={mode}
        initialData={selectedBanner}
        fetchBanners={fetchBanners}
      />

      <ViewBannerModal
        open={viewModalOpen}
        banner={bannerToView}
        handleClose={() => setViewModalOpen(false)}
      />

      {/* Search + Add Button Section */}
      <div className="flex flex-col items-center justify-between space-y-4 rounded-2xl bg-white p-5 shadow-sm md:flex-row md:space-y-0">
        {/* Search Box */}
        <div className="relative w-full md:w-1/2">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
            <HiSearch color="#c51369" className="h-5 w-5" />
          </span>
          <input
            type="text"
            placeholder="Search by banner name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border border-gray-300 px-4 py-2 pl-10 text-gray-700 transition-all focus:border-[#c51369] focus:ring-1 focus:ring-[#c51369]"
          />
        </div>

        {/* Add Button */}
        {currentTabPermissions?.create && (
          <button
            onClick={handleAdd}
            className="fixed bottom-6 right-6 z-50 flex items-center justify-center rounded-full bg-[#c51369] p-3 text-white hover:bg-[#a10f5b] hover:shadow-lg md:static"
            title="Add Banner"
          >
            <HiPlus className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="justify-left mt-5 flex gap-4 overflow-x-auto border-b border-gray-200">
        {["Home", "Buddies", "Gangs"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            className={`px-4 py-2 font-medium tracking-wide ${
              activeTab === tab
                ? "border-b-4 border-[#c51369] text-[#c51369]"
                : "text-gray-500 hover:text-[#c51369]"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Banner Cards */}
      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          <BannerSkeleton />
        ) : filteredBanners.length > 0 ? (
          filteredBanners.map((banner) => (
            <div
              key={banner._id}
              className="relative rounded-2xl border bg-white p-4 shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
            >
              <img
                src={banner.img}
                alt={banner.name}
                className="mb-3 h-40 w-full rounded-xl object-contain transition-transform duration-300 hover:scale-105 sm:h-48"
              />

              {/* ✅ Name + Status */}
              <h3 className="text-lg font-semibold text-gray-800">
                {banner.name}
              </h3>

              {/* ✅ Other Details */}
              <p className="text-sm text-gray-500">
                Valid Till:{" "}
                {banner.validTill
                  ? new Date(banner.validTill).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "N/A"}
              </p>

              <div className="mt-1 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Description: {banner.description}
                </p>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-semibold  ${
                    banner.isActive
                      ? " bg-green-500 text-white"
                      : "bg-red-500 text-white"
                  }`}
                >
                  {banner.isActive ? "ACTIVE" : "EXPIRED"}
                </span>
              </div>

              {/* Action Buttons */}
              <div className="absolute right-3 top-3 flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
                <button
                  onClick={() => handleView(banner)}
                  className="rounded-full bg-gray-100 p-2 transition duration-200 hover:bg-gray-200"
                >
                  <FaEye className="text-gray-600" />
                </button>
                {currentTabPermissions?.edit && (
                  <button
                    onClick={() => handleEdit(banner)}
                    className="rounded-full bg-gray-100 p-2 transition duration-200 hover:bg-gray-200"
                  >
                    <FaEdit className="text-blue-600" />
                  </button>
                )}
                {currentTabPermissions?.delete && (
                  <button
                    onClick={() => handleDelete(banner._id)}
                    className="rounded-full bg-gray-100 p-2 transition duration-200 hover:bg-gray-200"
                  >
                    <FaTrash className="text-red-600" />
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <p className="col-span-full text-center text-gray-500">
            No banners found.
          </p>
        )}
      </div>

      {/* ✅ Pagination */}
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default BannersList;
